import React from 'react';
import {
    Box,
    Typography,
    Divider,
    Button,
    Alert,
    Paper,
    Chip,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export interface VerificationResultDisplayProps {
    data: Record<string, any> | null;
    title: string;
    requestId?: string;
    success: boolean;
    message: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatKey = (key: string): string => {
    if (key.toLowerCase().includes('base64string') && key.toLowerCase().includes('image')) {
        return 'Profile Image';
    }
    return key
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
};

const isIsoDate = (v: string): boolean =>
    /^\d{4}-\d{2}-\d{2}T[\d:.+Z-]+$/.test(v);

const formatDate = (v: string): string => {
    try {
        return new Date(v).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
        return v;
    }
};

const isScalar = (v: any): boolean =>
    v === null || v === undefined || typeof v !== 'object';

const isEmptyValue = (v: any): boolean =>
    v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

const isEmptyItem = (item: any): boolean => {
    if (isScalar(item)) return isEmptyValue(item);
    if (Array.isArray(item)) return item.length === 0;
    if (typeof item === 'object') return Object.values(item).every(isEmptyValue);
    return false;
};

const filterNonEmptyItems = (items: any[]): any[] =>
    items.filter(item => !isEmptyItem(item));

const getMimeTypeFromBase64 = (b64: string): string => {
    if (b64.startsWith('iVBORw0KGgo')) return 'image/png';
    if (b64.startsWith('/9j/')) return 'image/jpeg';
    if (b64.startsWith('R0lGODdh') || b64.startsWith('R0lGODlh')) return 'image/gif';
    if (b64.startsWith('JVBERi0')) return 'application/pdf';
    return 'image/jpeg'; // fallback
};

// ── Scalar leaf renderer ─────────────────────────────────────────────────────

const ScalarValue: React.FC<{ value: any; fieldKey?: string }> = ({ value, fieldKey = '' }) => {
    if (value === null || value === undefined) {
        return <Typography variant="body2" sx={{ color: '#bbb', fontStyle: 'italic' }}>—</Typography>;
    }
    const str = String(value).trim();
    if (str.toLowerCase() === 'not available') {
        return <Typography variant="body2" sx={{ color: '#b0b0b0', fontStyle: 'italic' }}>Not Available</Typography>;
    }

    const k = fieldKey.toLowerCase();
    if ((k.includes('base64') || k.includes('photo') || k.includes('image')) && str.length > 100) {
        const mimeType = getMimeTypeFromBase64(str);
        if (mimeType !== 'application/pdf') {
            return (
                <Box sx={{ mt: 1, mb: 1 }}>
                    <img 
                        src={`data:${mimeType};base64,${str}`} 
                        alt={formatKey(fieldKey)} 
                        style={{ maxWidth: '100%', maxHeight: '250px', borderRadius: '8px', border: '1px solid #e0e8f4' }} 
                    />
                </Box>
            );
        }
    }

    if (isIsoDate(str)) {
        return <Typography variant="body2" sx={{ color: '#2c2c2c' }}>{formatDate(str)}</Typography>;
    }
    return <Typography variant="body2" sx={{ color: '#1a1a2e', wordBreak: 'break-word' }}>{str}</Typography>;
};

// ── Recursive value renderer ─────────────────────────────────────────────────
// Forward-declared so ObjectBlock and ArrayBlock can reference it.

function RecursiveValue({ value, depth = 0, fieldKey = '' }: { value: any; depth?: number; fieldKey?: string }): React.ReactElement {
    if (isScalar(value)) return <ScalarValue value={value} fieldKey={fieldKey} />;
    if (Array.isArray(value)) return <ArrayBlock items={value} depth={depth} />;
    return <ObjectBlock data={value} depth={depth} />;
}

// ── Object block ─────────────────────────────────────────────────────────────

function ObjectBlock({ data, depth = 0 }: { data: Record<string, any>; depth?: number }): React.ReactElement {
    const entries = Object.entries(data);
    return (
        <Box
            sx={{
                borderLeft: depth > 0 ? '2px solid #dce6f5' : 'none',
                pl: depth > 0 ? 1.5 : 0,
                ml: depth > 0 ? 0.5 : 0,
            }}
        >
            {entries.map(([key, val]) => {
                const label = formatKey(key);
                if (isScalar(val)) {
                    return (
                        <Box
                            key={key}
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                py: '7px',
                                borderBottom: '1px solid #f4f4f4',
                                '&:last-child': { borderBottom: 'none' },
                                gap: { xs: '2px', sm: 0 },
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#4a5568',
                                    width: { sm: depth > 0 ? '44%' : '42%' },
                                    flexShrink: 0,
                                    fontSize: '0.78rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.03em',
                                    pt: '1px',
                                }}
                            >
                                {label}
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                                <ScalarValue value={val} fieldKey={key} />
                            </Box>
                        </Box>
                    );
                }

                // Complex child — render as a labelled sub-section
                return (
                    <Box key={key} sx={{ py: '8px', borderBottom: '1px solid #f4f4f4', '&:last-child': { borderBottom: 'none' } }}>
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                fontWeight: 700,
                                color: '#0A1D56',
                                fontSize: '0.72rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                mb: 0.75,
                            }}
                        >
                            {label}
                            {Array.isArray(val) && (
                                <Box
                                    component="span"
                                    sx={{
                                        ml: 1,
                                        backgroundColor: '#EFF3FB',
                                        color: '#0A1D56',
                                        borderRadius: '10px',
                                        px: '6px',
                                        py: '1px',
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    {filterNonEmptyItems(val).length}
                                </Box>
                            )}
                        </Typography>
                        <RecursiveValue value={val} depth={depth + 1} fieldKey={key} />
                    </Box>
                );
            })}
        </Box>
    );
}

// ── Array block ───────────────────────────────────────────────────────────────

function ArrayBlock({ items, depth = 0 }: { items: any[]; depth?: number }): React.ReactElement {
    const filtered = filterNonEmptyItems(items);

    if (filtered.length === 0) {
        return <Typography variant="body2" sx={{ color: '#bbb', fontStyle: 'italic' }}>Empty</Typography>;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filtered.map((item, i) => (
                <Box
                    key={i}
                    sx={{
                        border: '1px solid #e8eef8',
                        borderRadius: '6px',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: '#f4f7fd',
                            px: 1.5,
                            py: '4px',
                            borderBottom: '1px solid #e8eef8',
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, color: '#4a5568', fontSize: '0.7rem', letterSpacing: '0.04em' }}
                        >
                            # {i + 1}
                        </Typography>
                    </Box>
                    <Box sx={{ px: 1.5, py: 0.5 }}>
                        <RecursiveValue value={item} depth={depth + 1} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

// ── Top-level section card ────────────────────────────────────────────────────

const Section: React.FC<{ heading: string; fields: [string, any][] }> = ({ heading, fields }) => (
    <Box sx={{ mb: 3 }}>
        <Box sx={{ backgroundColor: '#EFF3FB', borderRadius: '8px 8px 0 0', px: 2, py: '7px', borderLeft: '4px solid #0A1D56' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0A1D56', fontSize: '0.8rem', letterSpacing: '0.05em' }}>
                {heading.toUpperCase()}
            </Typography>
        </Box>
        <Paper variant="outlined" sx={{ borderRadius: '0 0 8px 8px', border: '1px solid #e0e8f4', borderTop: 'none', px: 2, pt: 0.5, pb: 0.5 }}>
            {fields.map(([key, val]) => {
                const label = formatKey(key);
                if (isScalar(val)) {
                    // Scalar → compact 2-column row
                    return (
                        <Box
                            key={key}
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { sm: 'flex-start' },
                                py: '10px',
                                borderBottom: '1px solid #f0f0f0',
                                '&:last-child': { borderBottom: 'none' },
                                gap: { xs: '2px', sm: 0 },
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#4a5568',
                                    width: { sm: '42%' },
                                    flexShrink: 0,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.03em',
                                    pt: '1px',
                                }}
                            >
                                {label}
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                                <ScalarValue value={val} fieldKey={key} />
                            </Box>
                        </Box>
                    );
                }

                // Complex → full-width block with sub-heading
                return (
                    <Box
                        key={key}
                        sx={{
                            py: '12px',
                            borderBottom: '1px solid #f0f0f0',
                            '&:last-child': { borderBottom: 'none' },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    color: '#0A1D56',
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                }}
                            >
                                {label}
                            </Typography>
                            {Array.isArray(val) && (
                                <Chip
                                    label={`${filterNonEmptyItems(val).length} item${filterNonEmptyItems(val).length !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{ height: '18px', fontSize: '0.65rem', backgroundColor: '#EFF3FB', color: '#0A1D56', fontWeight: 700 }}
                                />
                            )}
                        </Box>
                        <RecursiveValue value={val} depth={1} fieldKey={key} />
                    </Box>
                );
            })}
        </Paper>
    </Box>
);

// ── PDF Export ────────────────────────────────────────────────────────────────

function buildPrintValueHtml(value: any, depth: number = 0): string {
    if (value === null || value === undefined) return '<span class="na">—</span>';

    if (Array.isArray(value)) {
        const filtered = filterNonEmptyItems(value);
        if (filtered.length === 0) return '<span class="na">Empty</span>';
        return filtered
            .map(
                (item, i) => `
        <div class="array-item">
          <div class="array-item-header"># ${i + 1}</div>
          <div class="array-item-body">${buildPrintValueHtml(item, depth + 1)}</div>
        </div>`
            )
            .join('');
    }

    if (typeof value === 'object') {
        const indent = depth > 0 ? `style="padding-left:${depth * 12}px; border-left: 2px solid #d8e2f0; margin-left: 4px;"` : '';
        return `<div ${indent}><table class="nested-obj">${Object.entries(value)
            .map(([k, v]) => {
                if (isScalar(v)) {
                    const str = String(v ?? '—');
                    const cls = str.toLowerCase() === 'not available' ? 'na' : '';
                    const k_lower = k.toLowerCase();
                    if ((k_lower.includes('base64') || k_lower.includes('photo') || k_lower.includes('image')) && str.length > 100) {
                        const mimeType = getMimeTypeFromBase64(str);
                        if (mimeType !== 'application/pdf') {
                            return `<tr>
                              <td class="label">${formatKey(k)}</td>
                              <td class="value"><img src="data:${mimeType};base64,${str}" style="max-width:200px; max-height:200px;" /></td>
                            </tr>`;
                        }
                    }
                    return `<tr>
              <td class="label">${formatKey(k)}</td>
              <td class="value ${cls}">${str}</td>
            </tr>`;
                }
                return `<tr>
            <td colspan="2" class="complex-layout">
              <div class="complex-heading">${formatKey(k)}${Array.isArray(v) ? ` <span class="badge">${filterNonEmptyItems(v).length}</span>` : ''}</div>
              <div class="complex-body">${buildPrintValueHtml(v, depth + 1)}</div>
            </td>
          </tr>`;
            })
            .join('')}</table></div>`;
    }

    const str = String(value).trim();
    const cls = str.toLowerCase() === 'not available' ? 'na' : '';
    return `<span class="${cls}">${str}</span>`;
}

const buildPrintHtml = (
    title: string,
    requestId: string | undefined,
    success: boolean,
    message: string,
    sections: { heading: string; fields: [string, any][] }[]
): string => {
    const sectionsHtml = sections
        .map(
            sec => `
      <div class="section">
        <div class="section-heading">${sec.heading.toUpperCase()}</div>
        <table>
          ${sec.fields
                    .map(([k, v]) => {
                        if (isScalar(v)) {
                            const str = String(v ?? '—');
                            const k_lower = k.toLowerCase();
                            if ((k_lower.includes('base64') || k_lower.includes('photo') || k_lower.includes('image')) && str.length > 100) {
                                const mimeType = getMimeTypeFromBase64(str);
                                if (mimeType !== 'application/pdf') {
                                    return `<tr>
                                      <td class="label">${formatKey(k)}</td>
                                      <td class="value"><img src="data:${mimeType};base64,${str}" style="max-width:200px; max-height:200px;" /></td>
                                    </tr>`;
                                }
                            }
                            return `<tr>
                <td class="label">${formatKey(k)}</td>
                <td class="value ${str.toLowerCase() === 'not available' ? 'na' : ''}">${str}</td>
              </tr>`;
                        }
                        return `<tr>
              <td colspan="2" class="complex-layout">
                <div class="complex-heading">${formatKey(k)}${Array.isArray(v) ? ` <span class="badge">${filterNonEmptyItems(v).length} items</span>` : ''}</div>
                <div class="complex-body">${buildPrintValueHtml(v, 1)}</div>
              </td>
            </tr>`;
                    })
                    .join('')}
        </table>
      </div>`
        )
        .join('');

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>${title} — Verification Report</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a2e; padding: 32px; font-size: 12px; }
    .report-header { border-bottom: 3px solid #0A1D56; padding-bottom: 16px; margin-bottom: 24px; }
    .report-title { font-size: 20px; font-weight: 700; color: #0A1D56; }
    .report-meta { font-size: 11px; color: #666; margin-top: 6px; }
    .status-badge { display: inline-block; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; margin-top: 6px; }
    .status-success { background: #e6f4ea; color: #2e7d32; }
    .status-failure { background: #fdecea; color: #c62828; }
    .section { margin-bottom: 28px; }
    .section-heading { background: #EFF3FB; border-left: 4px solid #0A1D56; padding: 6px 12px; font-size: 10px; font-weight: 700; color: #0A1D56; letter-spacing: 0.06em; }
    table { width: 100%; border-collapse: collapse; border: 1px solid #d8e2f0; }
    tr { border-bottom: 1px solid #eef2f8; }
    tr:last-child { border-bottom: none; }
    td { padding: 7px 10px; vertical-align: top; font-size: 11.5px; }
    td.label { font-weight: 600; color: #4a5568; text-transform: uppercase; font-size: 10px; letter-spacing: 0.04em; width: 32%; }
    td.complex-label { color: #0A1D56; }
    td.value { color: #1a1a2e; }
    .na { color: #b0b0b0; font-style: italic; }
    .badge { background: #EFF3FB; color: #0A1D56; border-radius: 8px; padding: 1px 6px; font-size: 9px; font-weight: 700; }
    td.complex-layout { padding: 12px 10px; }
    .complex-heading { font-weight: 700; color: #0A1D56; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; display: flex; align-items: center; gap: 8px; }
    .complex-body { width: 100%; }
    table.nested-obj { width: 100%; border: none; }
    .nested-obj tr { border-bottom: 1px solid #f0f0f0; }
    .array-item { border: 1px solid #e8eef8; border-radius: 4px; margin-bottom: 6px; overflow: hidden; }
    .array-item-header { background: #f4f7fd; padding: 3px 8px; font-size: 10px; font-weight: 700; color: #4a5568; border-bottom: 1px solid #e8eef8; }
    .array-item-body { padding: 4px 8px; }
    .footer { margin-top: 32px; border-top: 1px solid #ddd; padding-top: 12px; font-size: 10px; color: #999; text-align: right; }
  </style>
</head>
<body>
  <div class="report-header">
    <div class="report-title">${title} — Verification Report</div>
    <div class="report-meta">
      ${requestId ? `Request ID: <strong>${requestId}</strong> &nbsp;|&nbsp;` : ''}
      Generated: ${new Date().toLocaleString('en-ZA')}
    </div>
    <span class="status-badge ${success ? 'status-success' : 'status-failure'}">
      ${success ? '✓ Success' : '✗ Failed'} — ${message}
    </span>
  </div>
  ${sectionsHtml}
  <div class="footer">Gensize Verification Services &nbsp;·&nbsp; Confidential</div>
  <script>window.onload = function(){ window.print(); }</script>
</body>
</html>`;
};

// ── Grouping ──────────────────────────────────────────────────────────────────

const KNOWN_PREFIXES: Record<string, string> = {
    subscriber_: 'Subscriber Details',
    match_: 'Match Details',
    report_: 'Report Details',
};

const groupFields = (data: Record<string, any>) => {
    const groups: Record<string, [string, any][]> = { __main__: [] };
    for (const [key, val] of Object.entries(data)) {
        let placed = false;
        for (const [prefix, heading] of Object.entries(KNOWN_PREFIXES)) {
            if (key.startsWith(prefix)) {
                if (!groups[heading]) groups[heading] = [];
                groups[heading].push([key, val]);
                placed = true;
                break;
            }
        }
        if (!placed) groups['__main__'].push([key, val]);
    }
    return groups;
};

// ── Main Component ────────────────────────────────────────────────────────────

const VerificationResultDisplay: React.FC<VerificationResultDisplayProps> = ({
    data,
    title,
    requestId,
    success,
    message,
}) => {
    if (!data && !message) return null;

    const groups = data ? groupFields(data) : {};
    const mainFields = groups['__main__'] ?? [];
    const extraGroups = Object.entries(groups).filter(([k]) => k !== '__main__');

    const allSections: { heading: string; fields: [string, any][] }[] = [
        ...(mainFields.length > 0 ? [{ heading: 'Verification Result', fields: mainFields }] : []),
        ...extraGroups.map(([heading, fields]) => ({ heading, fields })),
    ];

    const handleExportPDF = () => {
        const win = window.open('', '_blank', 'width=900,height=700');
        if (!win) return;
        win.document.write(buildPrintHtml(title, requestId, success, message, allSections));
        win.document.close();
    };

    return (
        <Box sx={{ mt: 4 }} id="verification-result-display">
            <Alert
                severity={success ? 'success' : 'warning'}
                icon={success ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                sx={{ mb: 3, borderRadius: '8px', fontWeight: 500, fontSize: '0.9rem' }}
            >
                {message}
            </Alert>

            {data && (
                <Paper elevation={0} sx={{ border: '1px solid #dce6f5', borderRadius: '12px', overflow: 'hidden' }}>
                    {/* Header */}
                    <Box
                        sx={{
                            backgroundColor: '#0A1D56',
                            px: 3,
                            py: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>
                                {title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                                {requestId && (
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem' }}>
                                        Request ID: {requestId}
                                    </Typography>
                                )}
                                <Chip
                                    label={success ? 'Verified' : 'No Result'}
                                    size="small"
                                    sx={{
                                        backgroundColor: success ? 'rgba(76,175,80,0.25)' : 'rgba(255,152,0,0.25)',
                                        color: success ? '#a5d6a7' : '#ffcc80',
                                        fontWeight: 600,
                                        fontSize: '0.68rem',
                                        height: '20px',
                                    }}
                                />
                            </Box>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={handleExportPDF}
                            sx={{
                                borderColor: 'rgba(255,255,255,0.4)',
                                color: '#fff',
                                fontSize: '0.78rem',
                                '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
                            }}
                        >
                            Export PDF
                        </Button>
                    </Box>

                    <Divider sx={{ borderColor: '#dce6f5' }} />

                    <Box sx={{ p: 3 }}>
                        {allSections.map(sec => (
                            <Section key={sec.heading} heading={sec.heading} fields={sec.fields} />
                        ))}
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default VerificationResultDisplay;
